import { useMemo, useState } from "react";
import type { BudgetLineRow, TimeEntry, ProjectOwnerWithUser } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";
import { formatHours } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";

const PHASE_LABELS: Record<string, string> = {
  preprod: "Preproduksjon",
  production: "Produksjon",
  postprod: "Postproduksjon",
};

const TASK_STATUS_LABELS: Record<string, string> = {
  not_started: "Ikke påbegynt",
  in_progress: "Påbegynt",
  completed: "Venter godkjenning",
  approved: "Godkjent",
  rejected: "Avvist",
};

const TASK_STATUS_COLORS: Record<string, string> = {
  not_started: "bg-gray-100 text-gray-600",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

function phaseLabel(phase: string | null): string {
  if (!phase) return "—";
  return PHASE_LABELS[phase] ?? phase;
}

export interface TeamMemberTask {
  assignmentId: string;
  budgetLineId: string;
  phase: string | null;
  description: string | null;
  taskStatus: string;
  assignedHours: number;
  loggedHours: number;
}

export interface TeamMemberCounts {
  total: number;
  done: number;
  inProgress: number;
  notStarted: number;
  rejected: number;
}

export interface TeamMember {
  userId: string;
  user: { full_name: string; avatar_url: string | null } | null;
  roleLabel: string | null;
  counts: TeamMemberCounts;
  totalAssignedHours: number;
  totalLoggedHours: number;
  approvedHours: number;
  pct: number;
  tasks: TeamMemberTask[];
}

const DONE_STATUSES = new Set(["approved", "completed"]);
const IN_PROGRESS_STATUSES = new Set(["in_progress"]);
const REJECTED_STATUSES = new Set(["rejected"]);

export interface BuildTeamOverviewInput {
  budgetLines: BudgetLineRow[];
  timeEntries: TimeEntry[];
  userMap: Map<string, UserLookup>;
  productionLeadId?: string | null;
  owners?: ProjectOwnerWithUser[];
  advisorId?: string | null;
  allUsers?: UserLookup[];
}

/**
 * Group budget-line assignments by user. Pure function for testability.
 * - Task counts group "approved" and "completed" as done.
 * - Logged hours = sum of time_entries.duration_seconds, matched on budget_line_id + user_id.
 * - Progress pct = approved/completed assigned hours / total assigned hours.
 */
export function buildTeamOverview(input: BuildTeamOverviewInput): TeamMember[] {
  const { budgetLines, timeEntries, userMap, productionLeadId, owners, advisorId, allUsers } = input;

  // Index logged hours by "userId::budgetLineId"
  const loggedKey = (uid: string, blId: string) => `${uid}::${blId}`;
  const loggedByKey = new Map<string, number>();
  const loggedByUser = new Map<string, number>();
  for (const te of timeEntries) {
    const secs = te.duration_seconds ?? 0;
    if (!te.user_id || secs <= 0) continue;
    loggedByUser.set(te.user_id, (loggedByUser.get(te.user_id) ?? 0) + secs);
    if (te.budget_line_id) {
      const k = loggedKey(te.user_id, te.budget_line_id);
      loggedByKey.set(k, (loggedByKey.get(k) ?? 0) + secs);
    }
  }

  const members = new Map<string, TeamMember>();

  for (const bl of budgetLines) {
    for (const a of bl.assignments) {
      const loggedSecs = loggedByKey.get(loggedKey(a.user_id, bl.id)) ?? 0;
      const task: TeamMemberTask = {
        assignmentId: a.id,
        budgetLineId: bl.id,
        phase: bl.phase,
        description: bl.description,
        taskStatus: a.task_status,
        assignedHours: Number(a.hours ?? 0),
        loggedHours: loggedSecs / 3600,
      };

      let m = members.get(a.user_id);
      if (!m) {
        const user = a.user ?? userMap.get(a.user_id) ?? null;
        m = {
          userId: a.user_id,
          user: user ? { full_name: user.full_name, avatar_url: user.avatar_url } : null,
          roleLabel: null,
          counts: { total: 0, done: 0, inProgress: 0, notStarted: 0, rejected: 0 },
          totalAssignedHours: 0,
          totalLoggedHours: 0,
          approvedHours: 0,
          pct: 0,
          tasks: [],
        };
        members.set(a.user_id, m);
      }

      m.tasks.push(task);
      m.counts.total++;
      m.totalAssignedHours += task.assignedHours;
      if (DONE_STATUSES.has(task.taskStatus)) {
        m.counts.done++;
        m.approvedHours += task.assignedHours;
      } else if (IN_PROGRESS_STATUSES.has(task.taskStatus)) {
        m.counts.inProgress++;
      } else if (REJECTED_STATUSES.has(task.taskStatus)) {
        m.counts.rejected++;
      } else {
        m.counts.notStarted++;
      }
    }
  }

  // Fill in logged hours per member (all entries, not only matched budget lines)
  for (const [userId, m] of members) {
    m.totalLoggedHours = (loggedByUser.get(userId) ?? 0) / 3600;
    m.pct = m.totalAssignedHours > 0
      ? Math.round((m.approvedHours / m.totalAssignedHours) * 100)
      : 0;
  }

  // Attach role label
  const roleByUser = new Map<string, string>();
  if (productionLeadId) {
    roleByUser.set(productionLeadId, "Prosjektleder");
    // Hvis PL ikke har egne oppgaver, legg til som medlem så rollen vises
    if (!members.has(productionLeadId)) {
      const plUser = (allUsers ?? []).find((u) => u.id === productionLeadId) ?? userMap.get(productionLeadId) ?? null;
      members.set(productionLeadId, {
        userId: productionLeadId,
        user: plUser ? { full_name: plUser.full_name, avatar_url: plUser.avatar_url } : null,
        roleLabel: null,
        counts: { total: 0, done: 0, inProgress: 0, notStarted: 0, rejected: 0 },
        totalAssignedHours: 0,
        totalLoggedHours: 0,
        approvedHours: 0,
        pct: 0,
        tasks: [],
      });
    }
  }
  if (advisorId) {
    roleByUser.set(advisorId, "Kunderådgiver");
    // Hvis rådgiveren ikke har noen oppgaver, legg vedkommende til som medlem
    if (!members.has(advisorId)) {
      const advUser = (allUsers ?? []).find((u) => u.id === advisorId) ?? userMap.get(advisorId) ?? null;
      members.set(advisorId, {
        userId: advisorId,
        user: advUser ? { full_name: advUser.full_name, avatar_url: advUser.avatar_url } : null,
        roleLabel: null,
        counts: { total: 0, done: 0, inProgress: 0, notStarted: 0, rejected: 0 },
        totalAssignedHours: 0,
        totalLoggedHours: 0,
        approvedHours: 0,
        pct: 0,
        tasks: [],
      });
    }
  }
  for (const o of owners ?? []) {
    if (!roleByUser.has(o.user_id)) roleByUser.set(o.user_id, "Selger");
  }
  for (const [, m] of members) {
    m.roleLabel = roleByUser.get(m.userId) ?? null;
  }

  // Sort: role-bearers first (PL, CA, Selger), then by name
  const rolePriority = (label: string | null): number => {
    if (label === "Prosjektleder") return 0;
    if (label === "Kunderådgiver") return 1;
    if (label === "Selger") return 2;
    return 3;
  };
  return Array.from(members.values()).sort((a, b) => {
    const r = rolePriority(a.roleLabel) - rolePriority(b.roleLabel);
    if (r !== 0) return r;
    return (a.user?.full_name ?? "").localeCompare(b.user?.full_name ?? "");
  });
}

// ── Presentation ─────────────────────────────────────────────

function MemberTaskList({ tasks }: { tasks: TeamMember["tasks"] }) {
  if (tasks.length === 0) {
    return (
      <div className="pl-9 py-2 text-xs text-muted-foreground italic">
        Ingen tildelte oppgaver.
      </div>
    );
  }
  return (
    <ul className="pl-9 pb-2 space-y-1">
      {tasks.map((t) => (
        <li
          key={t.assignmentId}
          className="flex items-center gap-2 text-xs py-1"
        >
          <span className="flex-1 min-w-0 truncate">{t.description ?? "—"}</span>
          <span className="text-muted-foreground shrink-0">{phaseLabel(t.phase)}</span>
          <span className="tabular-nums text-muted-foreground shrink-0">
            {formatHours(t.loggedHours)} / {formatHours(t.assignedHours)}
          </span>
          <span
            className={cn(
              "text-[10px] px-1.5 py-0.5 rounded shrink-0",
              TASK_STATUS_COLORS[t.taskStatus] ?? "bg-gray-100 text-gray-600",
            )}
          >
            {TASK_STATUS_LABELS[t.taskStatus] ?? t.taskStatus}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MemberRow({ member }: { member: TeamMember }) {
  const [expanded, setExpanded] = useState(false);
  const chevron = expanded ? "chevron-down" : "chevron-right";

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2.5 py-1.5 text-left hover:bg-muted/40 rounded transition-colors"
      >
        <SrIcon name={chevron} className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <Avatar name={member.user?.full_name ?? "?"} avatarUrl={member.user?.avatar_url} size="sm" className="shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-sm truncate">{member.user?.full_name ?? "Ukjent"}</span>
        </div>
        <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
          {member.counts.total} {member.counts.total === 1 ? "oppgave" : "oppgaver"}
        </span>
      </button>
      {expanded && <MemberTaskList tasks={member.tasks} />}
    </div>
  );
}

export function ProjectTeamOverview({
  budgetLines,
  timeEntries,
  userMap,
  productionLeadId,
  owners,
  advisorId,
  allUsers,
}: BuildTeamOverviewInput) {
  // Vis alle som har minst én oppgave — også de som har en rolle (PL,
  // kunderådgiver, selger). Rolle-badgen vises ved siden av navnet i
  // MemberRow så det er tydelig at samme person også er rolleinnehaver.
  const members = useMemo(
    () =>
      buildTeamOverview({
        budgetLines,
        timeEntries,
        userMap,
        productionLeadId,
        owners,
        advisorId,
        allUsers,
      }).filter((m) => m.counts.total > 0),
    [budgetLines, timeEntries, userMap, productionLeadId, owners, advisorId, allUsers],
  );

  if (members.length === 0) return null;

  return (
    <div className="divide-y divide-border/50">
      {members.map((m) => (
        <MemberRow key={m.userId} member={m} />
      ))}
    </div>
  );
}

export default ProjectTeamOverview;
